import type { ChipTone } from "@/components/ui/chip";
import { canonicalStateChip } from "@/components/finding-badges";

/** A tracker link's state in words (the chip on a linked-work row). */
export function linkStateChip(link: {
  kind: string;
  category: string;
  creationState: string;
}): { label: string; tone: ChipTone } {
  if (link.creationState === "pending") {
    return { label: "Creating…", tone: "muted" };
  }
  if (link.creationState === "failed") {
    return { label: "Not created", tone: "critical" };
  }
  switch (link.category) {
    case "open":
      return { label: "Open", tone: "outline" };
    case "in_progress":
      return {
        label: link.kind === "pull_request" ? "PR open" : "In progress",
        tone: "primary",
      };
    case "done":
      return {
        label: link.kind === "pull_request" ? "Merged" : "Closed",
        tone: "helps",
      };
    case "wont_do":
      return { label: "Not planned", tone: "muted" };
    case "duplicate":
      return { label: "Duplicate", tone: "muted" };
    case "deleted":
      return { label: "Deleted", tone: "muted" };
    default:
      return { label: "State unknown", tone: "outline" };
  }
}

export function linkNoun(kind: string): string {
  return kind === "pull_request" ? "PR" : kind === "epic" ? "Epic" : "Issue";
}

/** Who did it, for timeline sentences. */
export function actorLabel(entry: {
  actorKind: string;
  actorName: string | null;
}): string {
  switch (entry.actorKind) {
    case "user":
      return entry.actorName ?? "Someone";
    case "run":
      return "A re-run";
    case "tracker":
      return "GitHub";
    case "cli":
      return entry.actorName ? `${entry.actorName} (CLI)` : "CLI sync";
    case "rule":
      return "An ownership rule";
    default:
      return "iamu";
  }
}

/** A lifecycle state in words ("Open" where the chip shows nothing). */
export function stateLabel(
  state: string | null | undefined,
  reason?: string | null,
): string {
  return canonicalStateChip(state, reason)?.label ?? "Open";
}

/** What a tracker category change reads like in the timeline. */
export function externalStateSentence(payload: {
  to?: unknown;
  action?: unknown;
  linkKind?: unknown;
}): string {
  if (payload.action === "reopened") return "was reopened";
  switch (payload.to) {
    case "done":
      return payload.linkKind === "pull_request"
        ? "was merged"
        : "was closed as completed";
    case "wont_do":
      return "was closed as not planned";
    case "duplicate":
      return "was closed as a duplicate";
    case "deleted":
      return "was deleted or moved";
    case "in_progress":
      return "is in progress";
    case "open":
      return "is open";
    default:
      return "changed state";
  }
}
